import {AddressBookEntry} from "components/src/addressbook/AddressBook";
import {AddressBookEntryDialog} from "components/src/addressbook/AddressBookEntryDialog";
import {ClearSelection} from "components/src/common/ClearSelection";
import {DialogCloseCause} from "components/src/common/Enums";
import {InvertSelection} from "components/src/common/InvertSelection";
import {useTranslationWithOverride} from "components/src/hook/UseTranslationWithOverride";
import {StringUtils} from "components/src/utils/StringUtils";
import {Bin as DeleteIcon, EditPencil as EditIcon, Plus as AddIcon} from "iconoir-react";
import {useState} from "react";
import {Navbar} from "react-bootstrap";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Modal from "react-bootstrap/Modal";
import Nav from "react-bootstrap/Nav";
import Table from "react-bootstrap/Table";
import {useTranslation} from "react-i18next";
import {UserPropertyProps} from "./UserProperty";

/**
 * The owner should handle the OnClose event. The owner is responsible for closing the dialog.
 */
type OnClose = (closeCause: DialogCloseCause, value?: string) => void;

interface AddressBookEditorPopupProps {
    defaultValue?: string
    readOnly: boolean
    userPropertyProps: UserPropertyProps
    onClose: OnClose
}

/*
 * Parses an address book JSON string (a JSON array of entries) into a list of entries.
 */
const parseAddressBook = (json?: string): AddressBookEntry[] => {
    return StringUtils.isEmpty(json) ? [] : JSON.parse(json as string) as AddressBookEntry[];
}

const fullName = (entry: AddressBookEntry): string => {
    return [entry.firstName, entry.lastName].filter(part => !StringUtils.isEmpty(part)).join(" ");
}

/**
 * Popup for viewing and editing an address book property (a JSON array of address book entries). On apply
 * the entries are serialized back to a JSON string and returned via onClose.
 */
export const AddressBookEditorPopup = ({
                                           defaultValue,
                                           readOnly,
                                           userPropertyProps,
                                           onClose
                                       }: AddressBookEditorPopupProps) =>
{
    /**
     * The address book entries being edited
     */
    const [entries, setEntries] = useState<AddressBookEntry[]>(parseAddressBook(defaultValue));
    /**
     * The selected entries (for bulk delete)
     */
    const [selectedEntries, setSelectedEntries] = useState<AddressBookEntry[]>([]);
    /**
     * The entry being edited or added. undefined when adding a new entry.
     */
    const [editEntry, setEditEntry] = useState<AddressBookEntry | undefined>(undefined);
    /**
     * Whether the add/edit entry dialog is shown
     */
    const [showDialog, setShowDialog] = useState<boolean>(false);
    /**
     * i18next translation hook
     */
    const {t} = useTranslationWithOverride();
    const {t: tu} = useTranslation('userproperties');

    const displayEntries = [...entries].sort((a, b) =>
        a.email.toLowerCase().localeCompare(b.email.toLowerCase()));

    const onHideDialog = () => {
        onClose(DialogCloseCause.Cancel);
    }

    const apply = () => {
        // strip any UI-only fields; persist only the address book entry fields
        const toStore = entries.map(({email, phoneNumbers, organizationName, firstName, lastName}) =>
            ({email, phoneNumbers, organizationName, firstName, lastName}));

        onClose(DialogCloseCause.Succes, JSON.stringify(toStore));
    }

    const onAdd = () => {
        setEditEntry(undefined);
        setShowDialog(true);
    }

    const onEdit = (entry: AddressBookEntry) => {
        setEditEntry(entry);
        setShowDialog(true);
    }

    const isSelected = (entry: AddressBookEntry): boolean =>
        selectedEntries.some(e => e.email.toLowerCase() === entry.email.toLowerCase());

    const onChangeSelect = (checked: boolean, entry: AddressBookEntry) => {
        setSelectedEntries(checked ?
            [...selectedEntries, entry] :
            selectedEntries.filter(e => e.email.toLowerCase() !== entry.email.toLowerCase())
        );
    }

    const invertSelection = () => {
        setSelectedEntries(displayEntries.filter(entry => !isSelected(entry)));
    }

    const onDeleteSelected = () => {
        const selectedEmails = new Set(selectedEntries.map(e => e.email.toLowerCase()));
        setEntries(current => current.filter(e => !selectedEmails.has(e.email.toLowerCase())));
        setSelectedEntries([]);
    }

    /*
     * Called by the dialog when an entry is added or edited. Upserts the entry keyed by email (the
     * original email is passed so a changed email replaces the right entry).
     */
    const onDialogSave = (entry: AddressBookEntry, originalEmail?: string) => {
        setEntries(current => {
            const key = (originalEmail ?? entry.email).toLowerCase();
            const without = current.filter(e => e.email.toLowerCase() !== key
                && e.email.toLowerCase() !== entry.email.toLowerCase());
            return [...without, entry];
        });
        setShowDialog(false);
    }

    return (
        <Modal
            show={true}
            onHide={() => onHideDialog()}
            size="xl"
            fullscreen="lg-down"
            centered={true}
            scrollable={true}
            contentClassName={"user-property-modal-dialog"}
        >
            <Modal.Header closeButton>
                <Modal.Title>{t(readOnly ? 'user-property.view' : 'user-property.edit')} {tu("userproperty." + userPropertyProps.propertyDetails.userPropertyDetails.name)}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {!readOnly &&
                    <div className="d-flex gap-2 mb-3">
                        <Button variant="success" onClick={onAdd}>
                            <AddIcon/> {t("address-book.add-contact")}
                        </Button>
                    </div>
                }

                <Table bordered hover>
                    <thead>
                    <tr className="text-nowrap">
                        {selectedEntries.length === 0 ?
                            <>
                                {!readOnly &&
                                    <>
                                        <th className="th-w-min"><InvertSelection invertSelectionEvent={invertSelection}/></th>
                                        <th className="th-w-min"/>
                                    </>
                                }
                                <th>{t("address-book.email")}</th>
                                <th>{t("address-book.phone-numbers")}</th>
                                <th>{t("address-book.name")}</th>
                                <th>{t("address-book.organization")}</th>
                            </>
                            :
                            <th colSpan={6}>
                                <Navbar className="bg-body-tertiary ps-2">
                                    {/* clears the selection when clicked */}
                                    <ClearSelection clearSelectionEvent={() => setSelectedEntries([])} checked={true}/>

                                    <Navbar.Text className="ms-2">{selectedEntries.length} selected</Navbar.Text>

                                    <div className="vr ms-4 me-2"></div>

                                    <Nav>
                                        <Nav.Link onClick={onDeleteSelected}>
                                            <DeleteIcon className="text-danger"/> {t("address-book.delete")}
                                        </Nav.Link>
                                    </Nav>
                                </Navbar>
                            </th>
                        }
                    </tr>
                    </thead>
                    <tbody>
                    {displayEntries.length === 0 &&
                        <tr><td colSpan={6} className="text-center text-secondary">{t("address-book.no-contacts")}</td></tr>
                    }
                    {displayEntries.map(entry => (
                        <tr key={entry.email}>
                            {!readOnly &&
                                <>
                                    <td className="td-w-min">
                                        <Form.Check
                                            type="checkbox"
                                            className="me-0"
                                            checked={isSelected(entry)}
                                            onChange={(e) => onChangeSelect(e.target.checked, entry)}
                                            inline
                                        />
                                    </td>
                                    <td className="th-w-min">
                                        <Button variant="link" className="p-0" title={t("address-book.edit-contact")}
                                                onClick={() => onEdit(entry)}>
                                            <EditIcon/>
                                        </Button>
                                    </td>
                                </>
                            }
                            <td className="user-select-all">{entry.email}</td>
                            <td className="user-select-all">{entry.phoneNumbers.join(", ")}</td>
                            <td>{fullName(entry)}</td>
                            <td>{entry.organizationName}</td>
                        </tr>
                    ))}
                    </tbody>
                </Table>
            </Modal.Body>

            {!readOnly &&
                <Modal.Footer>
                    <Button variant="success" onClick={() => apply()}>
                        {t('user-property.apply')}
                    </Button>
                </Modal.Footer>
            }

            {showDialog &&
                <AddressBookEntryDialog
                    editEntry={editEntry}
                    existingEmails={displayEntries.map(e => e.email.toLowerCase())}
                    onSave={onDialogSave}
                    onClose={() => setShowDialog(false)}
                />
            }
        </Modal>
    )
}
